import { useEffect, useState } from "react";
import { Link, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/i18n";
import { adminApi, fmtError, fmtDateShort, fmtEUR, isoDateInTz } from "@/lib/api";
import {
  LayoutDashboard, Building2, LogOut, Loader2, Plus, ArrowRight, Copy, Check,
  Sparkles, Users, CalendarDays, KeyRound, RotateCcw, Trash2, Eye, Lock, X, Mail, Phone, Inbox,
  Mic, Ban, TriangleAlert, Search,
} from "lucide-react";

function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/admin/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-shell">
      <aside className="fixed top-0 left-0 h-screen w-[260px] hidden lg:flex flex-col justify-between border-r border-black/5 bg-[#1A1A1A] text-white px-6 py-8">
        <div>
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src={ASSETS.logo} alt="AURA"
              className="h-11 w-11 rounded-full object-contain bg-white/10 ring-1 ring-white/15" />
            <div className="leading-tight">
              <div className="font-serif text-2xl tracking-tight">AURA</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">Super Admin</div>
            </div>
          </Link>
          <nav className="flex flex-col gap-1">
            <NavItem to="/admin" icon={LayoutDashboard} label="Dashboard" testId="dashboard" end />
            <NavItem to="/admin/centers" icon={Building2} label="Centri" testId="centers" />
            <NavItem to="/admin/leads" icon={Inbox} label="Richieste demo" testId="leads" />
            <NavItem to="/admin/voce" icon={Mic} label="Voce Aura" testId="voce" />
            <NavItem to="/admin/account" icon={KeyRound} label="Account" testId="account" />
          </nav>
        </div>
        <div className="border-t border-white/10 pt-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] flex items-center justify-center text-sm font-medium text-[#1A1A1A]">
              SA
            </div>
            <div className="leading-tight flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-white/60 truncate">{user?.email}</div>
            </div>
            <button data-testid="admin-logout" onClick={onLogout}
              className="h-9 w-9 rounded-full border border-white/15 text-white/60 hover:text-white hover:bg-white/5 flex items-center justify-center">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>
      <main className="lg:ml-[260px] px-6 md:px-10 py-10 md:py-14 max-w-[1240px]">{children}</main>
    </div>
  );
}

const NavItem = ({ to, icon: Icon, label, testId, end }) => (
  <Link to={to} data-testid={`admin-nav-${testId}`}
    className="flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm text-white/80 hover:bg-white/5 hover:text-white">
    <Icon size={16} /><span>{label}</span>
  </Link>
);


function StatsHome() {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminApi.stats().then(setStats).catch(() => {}); }, []);

  return (
    <div data-testid="admin-home">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Super Admin</div>
      <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
        Il tuo network <span className="italic">AURA</span> <Sparkles size={28} className="inline -mt-2 text-[#A78BFA]" />
      </h1>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat label="Centri totali" value={stats?.tenants ?? "—"} testId="stat-tenants" />
        <Stat label="Centri attivi" value={stats?.tenants_active ?? "—"} testId="stat-active" />
        <Stat label="Utenti" value={stats?.users ?? "—"} testId="stat-users" />
        <Stat label="Appuntamenti" value={stats?.appointments ?? "—"} testId="stat-appts" />
        <Link to="/admin/leads" className="block">
          <Stat label="Richieste demo da gestire" value={stats?.leads_open ?? "—"} testId="stat-leads" />
        </Link>
      </div>
      <div className="mt-10">
        <Link to="/admin/centers" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333]">
          Vai ai centri <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} className="rounded-3xl border border-black/5 bg-white p-6 tilt-card">
    <div className="text-[10px] tracking-[0.25em] uppercase text-[#737373]">{label}</div>
    <div className="mt-3 font-serif font-light text-4xl text-[#1A1A1A] leading-none">{value}</div>
  </div>
);


function CentersList() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [created, setCreated] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [pwdCenter, setPwdCenter] = useState(null);      // centro per "Imposta password"
  const [clientsCenter, setClientsCenter] = useState(null); // centro per "Vedi clienti"
  const [deleting, setDeleting] = useState(null);

  const load = () => adminApi.listTenants().then(setItems).catch((e) => setError(fmtError(e)));
  useEffect(() => { load(); }, []);

  const resetOwner = async (c) => {
    if (!window.confirm(`Generare una nuova password per la titolare di "${c.name}"? Quella attuale smetterà di funzionare.`)) return;
    setResetting(c.id);
    setError("");
    try {
      const res = await adminApi.resetOwnerPassword(c.id);
      setCreated({ name: c.name, owner_credentials: res.credentials, mode: "reset" });
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setResetting(null);
    }
  };

  const removeCenter = async (c) => {
    const typed = window.prompt(
      `⚠️ ATTENZIONE: eliminare "${c.name}" cancella DEFINITIVAMENTE tutti i suoi dati (clienti, appuntamenti, fidelity…). Non è reversibile.\n\nPer confermare, scrivi lo slug del centro:  ${c.slug}`
    );
    if (typed == null) return;
    if (typed.trim().toLowerCase() !== c.slug.toLowerCase()) {
      window.alert("Slug non corrispondente: eliminazione annullata.");
      return;
    }
    setDeleting(c.id);
    setError("");
    try {
      await adminApi.deleteTenant(c.id);
      load();
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div data-testid="centers-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Centri</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
            Tutti i <span className="italic">centri AURA.</span>
          </h1>
        </div>
        <button data-testid="add-center" onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333]">
          <Plus size={14} /> Nuovo centro
        </button>
      </div>

      {error && <div className="mb-4 text-sm text-[#B91C1C]">{error}</div>}

      {!items ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#A78BFA]" /></div>
      ) : (
        <div className="rounded-3xl border border-black/5 bg-white overflow-hidden" data-testid="centers-list">
          {items.length === 0 ? (
            <div className="p-10 text-center text-[#737373] font-light">Nessun centro ancora. Creane uno.</div>
          ) : (
            <ul className="divide-y divide-black/5">
              {items.map((c) => (
                <li key={c.id} data-testid={`center-${c.id}`} className="px-6 py-5 flex items-center gap-4 flex-wrap">
                  <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] text-white flex items-center justify-center font-serif text-lg">
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium text-[#1A1A1A]">{c.name}</div>
                    <div className="text-xs text-[#737373]">
                      /{c.slug} · piano {c.plan} · {fmtDateShort(c.created_at)}
                    </div>
                  </div>
                  <button onClick={() => setClientsCenter(c)}
                    title="Vedi le clienti registrate online di questo centro"
                    className="text-xs text-[#4A4A4A] flex items-center gap-4 hover:text-[#A78BFA] transition">
                    <span className="inline-flex items-center gap-1"><Users size={12} /> {c.clients_count}</span>
                    <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {c.appointments_count}</span>
                  </button>
                  <Link to={`/${c.slug}/login`} className="inline-flex items-center gap-1 text-xs bg-white border border-black/10 rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]">
                    /{c.slug}/login
                  </Link>
                  <button
                    data-testid={`view-clients-${c.id}`}
                    onClick={() => setClientsCenter(c)}
                    title="Vedi clienti registrate"
                    className="inline-flex items-center gap-1.5 text-xs bg-white border border-black/10 rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]"
                  >
                    <Eye size={12} /> Clienti
                  </button>
                  <button
                    data-testid={`reset-owner-${c.id}`}
                    onClick={() => resetOwner(c)}
                    disabled={resetting === c.id}
                    title="Genera una nuova password casuale per la titolare"
                    className="inline-flex items-center gap-1.5 text-xs bg-white border border-black/10 rounded-full px-3 py-1.5 hover:bg-[#FAFAFA] disabled:opacity-50"
                  >
                    {resetting === c.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                    Reset password
                  </button>
                  <button
                    data-testid={`set-password-${c.id}`}
                    onClick={() => setPwdCenter(c)}
                    title="Imposta una password scelta da te (emergenza)"
                    className="inline-flex items-center gap-1.5 text-xs bg-white border border-black/10 rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]"
                  >
                    <Lock size={12} /> Imposta password
                  </button>
                  <span className={`text-[10px] tracking-[0.2em] uppercase rounded-full px-2 py-0.5 border ${
                    c.status === "active" ? "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]"
                    : c.status === "suspended" ? "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
                    : "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30"
                  }`}>
                    {c.status}
                  </span>
                  <button
                    data-testid={`delete-center-${c.id}`}
                    onClick={() => removeCenter(c)}
                    disabled={deleting === c.id}
                    title="Elimina definitivamente il centro"
                    className="inline-flex items-center gap-1.5 text-xs text-[#B91C1C] border border-[#FCA5A5] rounded-full px-3 py-1.5 hover:bg-[#FEE2E2] disabled:opacity-50"
                  >
                    {deleting === c.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Elimina
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showNew && <NewCenterModal onClose={() => setShowNew(false)} onCreated={(c) => { setCreated(c); load(); setShowNew(false); }} />}
      {created && <CreatedCenterModal center={created} onClose={() => setCreated(null)} />}
      {pwdCenter && <SetPasswordModal center={pwdCenter} onClose={() => setPwdCenter(null)}
        onDone={(creds) => { setPwdCenter(null); setCreated({ name: pwdCenter.name, owner_credentials: creds, mode: "reset" }); }} />}
      {clientsCenter && <TenantClientsModal center={clientsCenter} onClose={() => setClientsCenter(null)} />}
    </div>
  );
}


function SetPasswordModal({ center, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("La password deve avere almeno 8 caratteri."); return; }
    setBusy(true); setError("");
    try {
      const res = await adminApi.setOwnerPassword(center.id, password);
      onDone(res.credentials);
    } catch (err) { setError(fmtError(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="set-password-modal" className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-3xl max-w-md w-full p-8 border border-black/5">
        <h2 className="font-serif text-2xl text-[#1A1A1A] mb-1">Imposta password</h2>
        <p className="text-sm text-[#737373] font-light mb-5">
          Accesso d'emergenza per la titolare di <strong>{center.name}</strong>. La vecchia password smetterà di funzionare.
        </p>
        <Field label="Nuova password (min 8 caratteri)">
          <input data-testid="set-password-input" type="text" autoFocus value={password}
            onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="es. Estetica2026!" />
        </Field>
        {error && <div className="mt-3 text-sm text-[#B91C1C]">{error}</div>}
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 bg-white border border-black/10 rounded-full px-5 py-3 text-sm">Annulla</button>
          <button type="submit" disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-3 text-sm disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />} Imposta
          </button>
        </div>
      </form>
    </div>
  );
}


function TenantClientsModal({ center, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    adminApi.tenantClients(center.id).then(setData).catch((e) => setError(fmtError(e)));
  }, [center.id]);

  const clients = (data?.clients || []).filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.phone || "").includes(s);
  });

  return (
    <div data-testid="tenant-clients-modal" className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col border border-black/5">
        <div className="flex items-start justify-between p-6 border-b border-black/5">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-1">Clienti registrate</div>
            <h2 className="font-serif text-2xl text-[#1A1A1A]">{center.name}</h2>
            <p className="text-xs text-[#737373] mt-1">Solo clienti iscritte dal sito. La rubrica completa del salone resta nel gestionale desktop.</p>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#1A1A1A]"><X size={20} /></button>
        </div>
        <div className="px-6 pt-4">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email o telefono…"
            className={inputCls} />
        </div>
        <div className="p-6 overflow-y-auto">
          {error && <div className="text-sm text-[#B91C1C]">{error}</div>}
          {!data && !error ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#A78BFA]" /></div>
          ) : clients.length === 0 ? (
            <div className="text-center text-[#737373] font-light py-10">
              {q ? "Nessuna cliente trovata." : "Nessuna cliente registrata online per questo centro."}
            </div>
          ) : (
            <ul className="divide-y divide-black/5">
              {clients.map((c) => (
                <li key={c.id} className="py-3 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] text-white flex items-center justify-center text-xs font-medium">
                    {(c.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1A1A1A]">{c.name || "—"}</div>
                    <div className="text-xs text-[#737373] flex flex-wrap gap-x-3">
                      {c.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {c.email}</span>}
                      {c.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {c.phone}</span>}
                    </div>
                  </div>
                  <div className="text-[11px] text-[#A3A3A3]">{fmtDateShort(c.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-6 py-3 border-t border-black/5 text-xs text-[#737373]">
          {data ? `${clients.length} di ${data.clients.length} clienti` : ""}
        </div>
      </div>
    </div>
  );
}


function NewCenterModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ slug: "", name: "", email: "", phone: "", address: "", plan: "atelier", owner_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const handle = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const c = await adminApi.createTenant({ ...form, slug: form.slug.toLowerCase().trim() });
      onCreated(c);
    } catch (err) { setError(fmtError(err)); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="new-center-modal" className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-3xl max-w-lg w-full p-8 border border-black/5">
        <h2 className="font-serif text-2xl text-[#1A1A1A] mb-5">Nuovo centro</h2>
        <div className="space-y-4">
          <Field label="Slug URL (es. lumiere)"><input data-testid="new-slug" required pattern="[a-z0-9-]+" value={form.slug} onChange={handle("slug")} className={inputCls} /></Field>
          <Field label="Nome del centro"><input data-testid="new-name" required value={form.name} onChange={handle("name")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email titolare"><input data-testid="new-email" type="email" required value={form.email} onChange={handle("email")} className={inputCls} /></Field>
            <Field label="Nome titolare"><input data-testid="new-owner" required value={form.owner_name} onChange={handle("owner_name")} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefono"><input data-testid="new-phone" value={form.phone} onChange={handle("phone")} className={inputCls} /></Field>
            <Field label="Piano">
              <select data-testid="new-plan" value={form.plan} onChange={handle("plan")} className={inputCls}>
                <option value="essence">Essence</option>
                <option value="atelier">Atelier</option>
                <option value="maison">Maison</option>
              </select>
            </Field>
          </div>
          <Field label="Indirizzo"><input data-testid="new-address" value={form.address} onChange={handle("address")} className={inputCls} /></Field>
        </div>
        {error && <div className="mt-4 text-sm text-[#B91C1C]">{error}</div>}
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 bg-white border border-black/10 rounded-full px-5 py-3 text-sm">Annulla</button>
          <button type="submit" data-testid="new-submit" disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-3 text-sm disabled:opacity-50">
            {submitting && <Loader2 size={14} className="animate-spin" />} Crea centro
          </button>
        </div>
      </form>
    </div>
  );
}


function CreatedCenterModal({ center, onClose }) {
  const [copied, setCopied] = useState(false);
  const creds = center.owner_credentials;
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { console.error("clipboard copy failed", e); }
  };

  return (
    <div data-testid="created-modal" className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 border border-black/5">
        <div className="h-14 w-14 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] flex items-center justify-center">
          <Sparkles className="text-white" size={26} />
        </div>
        <h2 className="font-serif text-3xl text-[#1A1A1A] text-center">
          {center.mode === "reset" ? "Nuova password generata" : "Centro creato ✨"}
        </h2>
        <p className="mt-2 text-center text-[#4A4A4A] font-light text-sm">
          {center.mode === "reset"
            ? "Consegna subito queste credenziali alla titolare: la vecchia password non funziona più."
            : "Manda queste credenziali al titolare. La password è temporanea: chiedi di cambiarla al primo login."}
        </p>
        <div className="mt-6 bg-[#FAFAFA] rounded-2xl p-5 border border-black/5 text-sm space-y-2">
          <div><span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">Centro</span> <span className="font-medium">{center.name}</span></div>
          <div><span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">Login URL</span> <code>{creds.login_url}</code></div>
          <div><span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">Email</span> <code>{creds.email}</code></div>
          <div><span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">Password</span> <code data-testid="created-password">{creds.password}</code></div>
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={() => copy(`URL: ${creds.login_url}\nEmail: ${creds.email}\nPassword: ${creds.password}`)}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-black/10 rounded-full px-5 py-3 text-sm hover:bg-[#FAFAFA]">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copiato" : "Copia credenziali"}
          </button>
          <button onClick={onClose} className="flex-1 bg-[#1A1A1A] text-white rounded-full px-5 py-3 text-sm hover:bg-[#333]">Chiudi</button>
        </div>
      </div>
    </div>
  );
}


const inputCls = "w-full bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#A78BFA]";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">{label}</span>
    {children}
  </label>
);


function AccountSettings() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ new_email: "", new_password: "", confirm: "", current_password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const handle = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => { if (user?.email) setForm((s) => ({ ...s, new_email: user.email })); }, [user?.email]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setDone("");
    const email = form.new_email.trim().toLowerCase();
    const payload = { current_password: form.current_password };
    if (email && email !== user?.email) payload.new_email = email;
    if (form.new_password) {
      if (form.new_password !== form.confirm) { setError("Le due password nuove non coincidono."); return; }
      payload.new_password = form.new_password;
    }
    if (!payload.new_email && !payload.new_password) { setError("Non hai modificato nulla."); return; }

    setSubmitting(true);
    try {
      const r = await adminApi.updateAccount(payload);
      await refresh();
      setForm({ new_email: r.email, new_password: "", confirm: "", current_password: "" });
      setDone(r.email_changed && r.password_changed ? "Email e password aggiornate."
        : r.password_changed ? "Password aggiornata." : "Email aggiornata.");
    } catch (err) { setError(fmtError(err)); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="account-page" className="max-w-[640px]">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Account</div>
      <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
        Le tue <span className="italic">credenziali.</span>
      </h1>
      <p className="mt-4 text-[#4A4A4A] font-light text-sm">
        Sono le chiavi del pannello super admin: da qui gestisci tutti i centri. Cambiale
        appena puoi se stai ancora usando quelle iniziali.
      </p>

      <form onSubmit={submit} className="mt-10 rounded-3xl border border-black/5 bg-white p-8 space-y-4">
        <Field label="Email di accesso">
          <input data-testid="account-email" type="email" required value={form.new_email}
            onChange={handle("new_email")} className={inputCls} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nuova password (min. 10 caratteri)">
            <input data-testid="account-new-password" type="password" minLength={10} autoComplete="new-password"
              placeholder="lascia vuoto per non cambiarla"
              value={form.new_password} onChange={handle("new_password")} className={inputCls} />
          </Field>
          <Field label="Ripeti nuova password">
            <input data-testid="account-confirm" type="password" autoComplete="new-password"
              value={form.confirm} onChange={handle("confirm")} className={inputCls} />
          </Field>
        </div>

        <div className="pt-2 border-t border-black/5">
          <Field label="Password attuale (per conferma)">
            <input data-testid="account-current-password" type="password" required autoComplete="current-password"
              value={form.current_password} onChange={handle("current_password")} className={inputCls} />
          </Field>
        </div>

        {error && <div data-testid="account-error" className="text-sm text-[#B91C1C]">{error}</div>}
        {done && (
          <div data-testid="account-done" className="inline-flex items-center gap-2 text-sm text-[#047857]">
            <Check size={14} /> {done}
          </div>
        )}

        <button type="submit" data-testid="account-submit" disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-3 text-sm hover:bg-[#333] disabled:opacity-50">
          {submitting && <Loader2 size={14} className="animate-spin" />} Salva credenziali
        </button>
      </form>
    </div>
  );
}


// Richieste demo arrivate dal modulo della landing: prima restavano solo nel
// database (e nell'email, se configurata).
const PLAN_LABEL = { essence: "Essence", atelier: "Atelier", maison: "Maison" };

function LeadsList() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("open");
  const [busy, setBusy] = useState(null);

  const load = () => adminApi.leads().then(setItems).catch((e) => setError(fmtError(e)));
  useEffect(() => { load(); }, []);

  const toggle = async (l) => {
    if (busy) return;
    setBusy(l.id);
    setError("");
    try {
      await adminApi.updateLead(l.id, { handled: !l.handled });
      await load();
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setBusy(null);
    }
  };

  const shown = (items || []).filter((l) => (filter === "open" ? !l.handled : filter === "done" ? l.handled : true));
  const openCount = (items || []).filter((l) => !l.handled).length;

  return (
    <div data-testid="admin-leads">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Super Admin</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
            Richieste <span className="italic">demo.</span>
          </h1>
          <p className="mt-3 text-sm text-[#4A4A4A] font-light">
            {items ? `${openCount} da ricontattare` : "Caricamento…"} · la pagina promette un ricontatto entro 24 ore.
          </p>
        </div>
        <div className="inline-flex bg-white border border-black/5 rounded-full p-1 text-xs">
          {[{ k: "open", l: "Da gestire" }, { k: "done", l: "Gestite" }, { k: "all", l: "Tutte" }].map((f) => (
            <button key={f.k} data-testid={`leads-filter-${f.k}`} onClick={() => setFilter(f.k)}
              className={`px-4 py-1.5 rounded-full ${filter === f.k ? "bg-[#1A1A1A] text-white" : "text-[#737373]"}`}>
              {f.l}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="mb-6 text-sm text-[#B91C1C]">{error}</div>}
      {!items ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>
      ) : shown.length === 0 ? (
        <div className="rounded-3xl border border-black/5 bg-white p-12 text-center text-[#737373] font-light">
          Nessuna richiesta in questa vista.
        </div>
      ) : (
        <ul className="space-y-3">
          {shown.map((l) => (
            <li key={l.id} data-testid={`lead-${l.id}`}
              className={`rounded-3xl border border-black/5 bg-white p-6 ${l.handled ? "opacity-60" : ""}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="font-medium text-[#1A1A1A]">
                    {l.name}{l.salon ? <span className="text-[#737373] font-light"> · {l.salon}</span> : null}
                  </div>
                  <div className="mt-1 text-xs text-[#737373] flex flex-wrap gap-x-4 gap-y-1">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-[#1A1A1A]"><Mail size={11} /> {l.email}</a>
                    {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-[#1A1A1A]"><Phone size={11} /> {l.phone}</a>}
                    {l.plan && <span>Piano: {PLAN_LABEL[l.plan] || l.plan}</span>}
                    <span>{fmtDateShort(l.created_at)}</span>
                  </div>
                  {l.message && (
                    <p className="mt-3 text-sm text-[#4A4A4A] font-light whitespace-pre-line break-words">{l.message}</p>
                  )}
                </div>
                <button onClick={() => toggle(l)} disabled={busy === l.id} data-testid={`lead-toggle-${l.id}`}
                  className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border disabled:opacity-50 ${
                    l.handled ? "border-black/10 text-[#737373] hover:bg-[#FAFAFA]" : "border-[#86EFAC] bg-[#F0FDF4] text-[#15803D]"}`}>
                  {busy === l.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  {l.handled ? "Riapri" : "Segna come gestita"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// Voce «Aura»: l'assistente vocale del gestionale parla con un agente ElevenLabs di
// OdyneAI, pagato dal produttore. Ogni domanda ad Aura è una chiamata: qui si vede
// quale licenza chiama e quanto, e le si può spegnere la voce o cambiare il tetto.
const TIPO_LICENZA = { full: "Completa", trial: "Prova" };
const pillola = "text-[10px] tracking-[0.2em] uppercase rounded-full px-2 py-0.5 border";
const PILLOLA_VERDE = `${pillola} bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]`;
const PILLOLA_ROSSA = `${pillola} bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]`;
const PILLOLA_GIALLA = `${pillola} bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30`;
const PILLOLA_GRIGIA = `${pillola} bg-[#FAFAFA] text-[#737373] border-black/10`;
const bottonePillola = "inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border disabled:opacity-50";

// "2026-12-31" → "31/12/2026" (senza passare da Date: niente scarti di fuso)
const giornoIt = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(s || "") ? s.split("-").reverse().join("/") : "—");
const quandoIt = (d) => {
  if (!d) return "mai";
  try {
    return new Intl.DateTimeFormat("it-IT", { dateStyle: "short", timeStyle: "short" }).format(new Date(d));
  } catch (_e) { return "—"; }
};
const idCorto = (id) => (id && id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id || "—");

function VoceAura() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = () => adminApi.voiceLicenze(30).then(setData).catch((e) => setError(fmtError(e)));
  useEffect(() => { load(); }, []);

  const salva = async (id, modifiche) => {
    if (busy) return;
    setBusy(id);
    setError("");
    try {
      await adminApi.voiceAggiorna(id, modifiche);
      await load();
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setBusy(null);
    }
  };

  // l = una riga dell'elenco, oppure { license_id } di una licenza appena letta e mai vista
  const cambiaBlocco = async (l) => {
    const nome = l.etichetta || l.centro || `la licenza ${idCorto(l.license_id)}`;
    if (l.bloccata) {
      if (!window.confirm(`Riaccendere la voce di Aura per ${nome}?`)) return;
      await salva(l.license_id, { bloccata: false });
      return;
    }
    const motivo = window.prompt(
      `Spegnere la voce di Aura per ${nome}?\n\nIl resto del gestionale continua a funzionare; Aura dirà che la voce è stata disattivata. Il blocco vale per tutto il computer, anche dopo un rinnovo della licenza.\n\nScrivi il motivo (facoltativo, lo vedi solo tu) e premi OK per confermare.`,
      ""
    );
    if (motivo == null) return;
    // una licenza appena letta e mai vista porta con sé l'ID del computer: così il blocco vale subito per tutto il computer
    await salva(l.license_id, {
      bloccata: true, motivo: motivo.trim().slice(0, 200),
      ...(l.machine_id ? { machine_id: l.machine_id } : {}),
    });
  };

  const items = data?.licenze || [];
  const oggi = isoDateInTz("Europe/Rome");
  // il blocco segue il computer: una licenza è ferma anche se è bloccata un'altra dello stesso PC
  const fermaDaAltra = (l) => !l.bloccata && !!l.machine_id
    && items.some((o) => o.bloccata && o.machine_id === l.machine_id);
  const ferma = (l) => l.bloccata || fermaDaAltra(l);
  const shown = items.filter((l) => (filter === "blocked" ? ferma(l) : filter === "active" ? !ferma(l) : true));
  const tettoDi = (l) => (l.tipo === "trial" ? data.tetto_prova : data.tetto_giornaliero);

  return (
    <div data-testid="admin-voce">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Super Admin</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
            Voce <span className="italic">Aura.</span>
          </h1>
          <p className="mt-3 text-sm text-[#4A4A4A] font-light max-w-[640px]">
            {data
              ? `${items.length} ${items.length === 1 ? "licenza" : "licenze"} in elenco · tetto: ${data.tetto_giornaliero} chiamate al giorno per computer (${data.tetto_prova} per le licenze di prova).`
              : "Caricamento…"}{" "}
            Ogni domanda ad Aura è una chiamata, e la paghi tu.
          </p>
        </div>
        <div className="inline-flex bg-white border border-black/5 rounded-full p-1 text-xs">
          {[{ k: "all", l: "Tutte" }, { k: "active", l: "Attive" }, { k: "blocked", l: "Bloccate" }].map((f) => (
            <button key={f.k} data-testid={`voce-filter-${f.k}`} onClick={() => setFilter(f.k)}
              className={`px-4 py-1.5 rounded-full ${filter === f.k ? "bg-[#1A1A1A] text-white" : "text-[#737373]"}`}>
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="mb-6 text-sm text-[#B91C1C]" data-testid="voce-error">{error}</div>}

      {data && !data.configurato && (
        <div data-testid="voce-non-configurato"
          className="mb-6 rounded-3xl border border-[#F59E0B]/30 bg-[#FEF3C7] p-5 text-sm text-[#92400E] flex items-start gap-3">
          <TriangleAlert size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Aura non è ancora accesa.</div>
            <div className="font-light mt-1">
              Sul server mancano la chiave ElevenLabs (<code>ELEVENLABS_API_KEY</code>) o l'ID dell'agente
              (<code>ELEVENLABS_AURA_AGENT_ID</code>). Inseriscile su Render, alla voce Environment: finché
              mancano, in tutti i centri Aura risponde che la voce non è disponibile.
            </div>
          </div>
        </div>
      )}

      {!data ? (
        !error && <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>
      ) : (
        <>
          <LeggiLicenza licenze={items} busy={busy} onCambiaBlocco={cambiaBlocco} />

          {shown.length === 0 ? (
            <div className="rounded-3xl border border-black/5 bg-white p-12 text-center text-[#737373] font-light">
              {items.length === 0
                ? "Nessuna licenza ha ancora chiamato Aura. Compariranno qui alla prima chiamata."
                : "Nessuna licenza in questa vista."}
            </div>
          ) : (
            <ul className="space-y-3" data-testid="voce-list">
              {shown.map((l) => (
                <LicenzaVoce key={l.license_id} l={l} tettoPredefinito={tettoDi(l)} scaduta={!!l.scade_il && l.scade_il < oggi}
                  fermaDaAltra={fermaDaAltra(l)} busy={busy === l.license_id}
                  onSalva={(m) => salva(l.license_id, m)} onCambiaBlocco={() => cambiaBlocco(l)} onErrore={setError} />
              ))}
            </ul>
          )}

          <p className="mt-6 text-xs text-[#737373] font-light">
            Il tetto conta le chiamate di ogni computer e riparte a mezzanotte. Per cambiarlo a tutti si usano le
            variabili <code>AURA_VOICE_DAILY_LIMIT</code> e <code>AURA_VOICE_DAILY_LIMIT_TRIAL</code> su Render;
            qui puoi dare un tetto diverso a una singola licenza.
          </p>
        </>
      )}
    </div>
  );
}


function LicenzaVoce({ l, tettoPredefinito, scaduta, fermaDaAltra, busy, onSalva, onCambiaBlocco, onErrore }) {
  const [nome, setNome] = useState(l.etichetta || "");
  const [tetto, setTetto] = useState(l.limite_giornaliero ?? "");
  // dopo un salvataggio l'elenco si ricarica: i campi tornano a ciò che è salvato
  useEffect(() => { setNome(l.etichetta || ""); }, [l.etichetta]);
  useEffect(() => { setTetto(l.limite_giornaliero ?? ""); }, [l.limite_giornaliero]);

  const nomeCambiato = nome.trim() !== (l.etichetta || "");
  const tettoCambiato = String(tetto).trim() !== String(l.limite_giornaliero ?? "");
  const limite = l.limite_giornaliero ?? tettoPredefinito;
  const esaurito = l.oggi >= limite;

  const salvaTetto = () => {
    const testo = String(tetto).trim();
    const n = testo === "" ? null : Number(testo);
    if (n !== null && (!Number.isInteger(n) || n < 0 || n > 2000)) {
      onErrore("Il tetto deve essere un numero intero tra 0 e 2000 (vuoto = tetto di tutti).");
      return;
    }
    onSalva({ limite_giornaliero: n });
  };

  return (
    <li data-testid={`voce-licenza-${l.license_id}`}
      className={`rounded-3xl border border-black/5 bg-white p-6 ${l.bloccata || fermaDaAltra ? "opacity-70" : ""}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <input data-testid={`voce-etichetta-${l.license_id}`} value={nome} maxLength={80}
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && nomeCambiato) onSalva({ etichetta: nome.trim() }); }}
              placeholder={l.centro || "Scrivi il nome del centro…"}
              title="Nome che dai tu a questa licenza (lo vedi solo tu)"
              className="font-medium text-[#1A1A1A] bg-transparent border-b border-dashed border-black/15 focus:border-[#A78BFA] focus:outline-none py-0.5 min-w-[200px] placeholder:font-light placeholder:text-[#A3A3A3]" />
            {nomeCambiato && (
              <button data-testid={`voce-etichetta-salva-${l.license_id}`} disabled={busy}
                onClick={() => onSalva({ etichetta: nome.trim() })}
                className={`${bottonePillola} border-black/10 text-[#4A4A4A] hover:bg-[#FAFAFA]`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Salva nome
              </button>
            )}
            <span className={l.tipo === "trial" ? PILLOLA_GIALLA : PILLOLA_GRIGIA}>
              {TIPO_LICENZA[l.tipo] || "Mai vista"}
            </span>
            {l.bloccata ? <span className={PILLOLA_ROSSA}>Bloccata</span>
              : fermaDaAltra ? <span className={PILLOLA_ROSSA} title="Sullo stesso computer c'è una licenza bloccata: sblocca quella">Computer bloccato</span>
              : scaduta ? <span className={PILLOLA_GIALLA}>Scaduta</span>
              : <span className={PILLOLA_VERDE}>Attiva</span>}
          </div>
          <div className="mt-2 text-xs text-[#737373] flex flex-wrap gap-x-4 gap-y-1">
            {l.centro && <span className="inline-flex items-center gap-1"><Building2 size={11} /> {l.centro}</span>}
            <span title={`ID licenza: ${l.license_id}`}>Licenza <code>{l.license_id}</code></span>
            {l.scade_il && <span>Scade il {giornoIt(l.scade_il)}</span>}
            <span>Ultima chiamata: {quandoIt(l.last_seen)}</span>
            {l.versione && <span>AURA {l.versione}</span>}
          </div>
          {l.bloccata && l.motivo && (
            <p className="mt-2 text-xs text-[#B91C1C]">Motivo del blocco: {l.motivo}</p>
          )}
          {l.machine_id && l.dispositivo_dichiarato && l.dispositivo_dichiarato !== l.machine_id && (
            <p className="mt-2 text-xs text-[#92400E] font-light" title={`Nella licenza: ${l.machine_id} · dichiarato: ${l.dispositivo_dichiarato}`}>
              L'ultima chiamata è arrivata da un computer con un ID diverso da quello scritto nella licenza
              (capita dopo certi rinnovi; se il consumo è strano, la chiave potrebbe essere stata copiata).
            </p>
          )}
        </div>

        <div className="flex items-center gap-5 flex-wrap">
          <div className="text-right" title="Chiamate di oggi su questo computer / tetto del giorno">
            <div className={`font-serif font-light text-3xl leading-none ${esaurito ? "text-[#B91C1C]" : "text-[#1A1A1A]"}`}>
              {l.oggi}<span className="text-base text-[#737373]"> / {limite}</span>
            </div>
            <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-[#737373]">oggi</div>
          </div>
          <div className="text-right">
            <div className="font-serif font-light text-3xl leading-none text-[#1A1A1A]">{l.ultimi_giorni}</div>
            <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-[#737373]">ultimi 30 giorni</div>
          </div>
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-1">Tetto suo</span>
            <span className="inline-flex items-center gap-2">
              <input data-testid={`voce-tetto-${l.license_id}`} type="number" min={0} max={2000} step={1}
                value={tetto} onChange={(e) => setTetto(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && tettoCambiato) salvaTetto(); }}
                placeholder={String(tettoPredefinito)}
                title="Chiamate al giorno solo per questa licenza. Vuoto = il tetto di tutti."
                className="w-20 bg-[#FAFAFA] border border-black/10 rounded-2xl px-3 py-1.5 text-sm focus:outline-none focus:border-[#A78BFA]" />
              {tettoCambiato && (
                <button data-testid={`voce-tetto-salva-${l.license_id}`} disabled={busy} onClick={salvaTetto}
                  className={`${bottonePillola} border-black/10 text-[#4A4A4A] hover:bg-[#FAFAFA]`}>
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Salva
                </button>
              )}
            </span>
          </label>
          <button data-testid={`voce-blocco-${l.license_id}`} onClick={onCambiaBlocco} disabled={busy}
            className={`${bottonePillola} ${l.bloccata
              ? "border-[#86EFAC] bg-[#F0FDF4] text-[#15803D]"
              : "border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEE2E2]"}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : l.bloccata ? <Check size={12} /> : <Ban size={12} />}
            {l.bloccata ? "Sblocca" : "Blocca"}
          </button>
        </div>
      </div>
    </li>
  );
}


// Il produttore incolla una chiave di licenza (quella mandata al cliente) e ne ricava
// l'ID: serve per bloccarla anche se quel centro non ha mai chiamato Aura.
function LeggiLicenza({ licenze, busy, onCambiaBlocco }) {
  const [chiave, setChiave] = useState("");
  const [esito, setEsito] = useState(null);
  const [leggendo, setLeggendo] = useState(false);
  const [error, setError] = useState("");

  const leggi = async (e) => {
    e.preventDefault();
    if (!chiave.trim() || leggendo) return;
    setLeggendo(true);
    setError("");
    setEsito(null);
    try {
      setEsito(await adminApi.voiceLeggiLicenza(chiave.trim()));
      setChiave("");   // la chiave non resta sulla pagina: serviva solo a ricavare l'ID
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setLeggendo(false);
    }
  };

  // se la licenza ha già chiamato Aura (o è già stata bloccata) è nell'elenco qui sotto
  const nota = esito?.valida ? licenze.find((x) => x.license_id === esito.license_id) : null;
  const bloccata = !!nota?.bloccata;

  return (
    <div data-testid="voce-leggi" className="mb-6 rounded-3xl border border-black/5 bg-white p-6">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Leggi una licenza</div>
      <p className="text-sm text-[#4A4A4A] font-light mb-4">
        Incolla la chiave di licenza di un centro (comincia con <code>AURA-</code>) per vedere il suo ID e, se
        serve, spegnerle la voce. La chiave viene solo letta: non resta salvata da nessuna parte.
      </p>
      <form onSubmit={leggi} className="flex gap-3 flex-wrap">
        <input data-testid="voce-leggi-input" value={chiave} onChange={(e) => setChiave(e.target.value)}
          placeholder="AURA-…" autoComplete="off" spellCheck={false}
          className={`${inputCls} flex-1 min-w-[240px] font-mono`} />
        <button type="submit" data-testid="voce-leggi-submit" disabled={leggendo || !chiave.trim()}
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333] disabled:opacity-50">
          {leggendo ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Leggi
        </button>
      </form>
      {error && <div className="mt-3 text-sm text-[#B91C1C]">{error}</div>}
      {esito && !esito.valida && (
        <div data-testid="voce-leggi-esito" className="mt-4 text-sm text-[#B91C1C]">
          Questa non è una licenza AURA valida: controlla di averla incollata tutta, senza pezzi mancanti.
        </div>
      )}
      {esito?.valida && (
        <div data-testid="voce-leggi-esito" className="mt-4 bg-[#FAFAFA] rounded-2xl p-5 border border-black/5 text-sm space-y-2">
          <div><span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">ID licenza</span> <code>{esito.license_id}</code></div>
          <div><span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">ID computer</span> <code className="break-all">{esito.machine_id}</code></div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">Tipo</span>
            <span className={esito.tipo === "trial" ? PILLOLA_GIALLA : PILLOLA_GRIGIA}>{TIPO_LICENZA[esito.tipo] || esito.tipo}</span>
            <span className="text-[#737373] text-xs uppercase tracking-[0.2em] ml-3 mr-2">Scade il</span>
            <span>{giornoIt(esito.scade_il)}</span>
            {esito.scaduta && <span className={PILLOLA_GIALLA}>Scaduta</span>}
            {bloccata && <span className={PILLOLA_ROSSA}>Bloccata</span>}
          </div>
          <div className="text-xs text-[#737373] font-light">
            {nota?.last_seen
              ? `Ha chiamato Aura l'ultima volta il ${quandoIt(nota.last_seen)}${nota.etichetta || nota.centro ? ` (${nota.etichetta || nota.centro})` : ""}.`
              : "Non ha ancora mai chiamato Aura."}
          </div>
          <div className="pt-2">
            <button data-testid="voce-leggi-blocco" disabled={busy === esito.license_id}
              onClick={() => onCambiaBlocco({ license_id: esito.license_id, bloccata: false, ...nota, machine_id: nota?.machine_id || esito.machine_id })}
              className={`${bottonePillola} ${bloccata
                ? "border-[#86EFAC] bg-[#F0FDF4] text-[#15803D]"
                : "border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEE2E2]"}`}>
              {busy === esito.license_id ? <Loader2 size={12} className="animate-spin" /> : bloccata ? <Check size={12} /> : <Ban size={12} />}
              {bloccata ? "Sblocca questa licenza" : "Blocca la voce per questa licenza"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdminDashboard() {
  return (
    <AdminShell>
      <Routes>
        <Route index element={<StatsHome />} />
        <Route path="centers" element={<CentersList />} />
        <Route path="leads" element={<LeadsList />} />
        <Route path="voce" element={<VoceAura />} />
        <Route path="account" element={<AccountSettings />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminShell>
  );
}
